import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ListAltIcon from '@mui/icons-material/ListAlt'
import { DataTable, DataTableColumn } from '@components/common/DataTable'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type { PerfSlowRequest } from '@api/types/performance'
import { usePerfSlowRequest } from '../hooks/usePerformance'
import { SqlBlock } from './SqlBlock'
import { formatCount, formatDateTime, formatMs, latencySeverity } from './format'

interface SlowRequestsTabProps {
  rows?: PerfSlowRequest[]
  loading: boolean
  error?: string | null
  onRetry: () => void
}

/**
 * Individual slow requests, and the queries each one issued.
 *
 * This is the tab that actually explains a slow endpoint. The roll-ups say where the time went in
 * aggregate; the query breakdown here says why, and the answer is very often visible in one glance —
 * a request with four hundred commands and one distinct shape among them is an N+1 loop, and no
 * average would ever have shown that.
 */
export function SlowRequestsTab({ rows, loading, error, onRetry }: SlowRequestsTabProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [openId, setOpenId] = useState<number | null>(null)

  const detail = usePerfSlowRequest(openId)
  const dialogProps = useMobileDialogProps()

  const columns: DataTableColumn<PerfSlowRequest>[] = [
    {
      key: 'path',
      header: 'Request',
      primary: true,
      render: (row) => (
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Chip label={row.method} size="small" variant="outlined" sx={{ minWidth: 56 }} />
            <Typography
              variant="body2"
              sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all' }}
            >
              {row.path}
            </Typography>
          </Stack>
          {row.exception && (
            <Typography variant="caption" color="error.main" sx={{ wordBreak: 'break-word' }}>
              {row.exception}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      key: 'when',
      header: 'When',
      hideBelow: 'md',
      render: (row) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDateTime(row.startedUtc)}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (row) => (
        <Chip
          label={row.statusCode}
          size="small"
          color={row.statusCode >= 500 ? 'error' : row.statusCode >= 400 ? 'warning' : 'default'}
        />
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" fontWeight="bold" color={`${latencySeverity(row.durationMs)}.main`}>
          {formatMs(row.durationMs)}
        </Typography>
      ),
    },
    {
      key: 'db',
      header: 'Database',
      align: 'right',
      render: (row) => (
        <Stack alignItems="flex-end">
          <Typography variant="body2">{formatMs(row.dbMs)}</Typography>
          <Typography
            variant="caption"
            // A three-figure query count on one request is the signature of a loop, so it is
            // coloured rather than left to be noticed.
            color={row.dbCommandCount > 50 ? 'error.main' : 'text.secondary'}
            fontWeight={row.dbCommandCount > 50 ? 'bold' : undefined}
          >
            {formatCount(row.dbCommandCount)} queries
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'service',
      header: 'Service',
      hideBelow: 'lg',
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.service}
        </Typography>
      ),
    },
  ]

  const current = detail.data

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        Requests kept whole because they crossed the slow threshold, or because they threw. Open one for the
        queries it issued.
      </Alert>

      <DataTable
        rows={rows ?? []}
        columns={columns}
        getRowKey={(row) => String(row.id)}
        loading={loading}
        error={error}
        onRetry={onRetry}
        emptyMessage="No slow requests in this window. That is the good outcome."
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => setOpenId(row.id)}
        // DataTable rows only respond to a pointer; the action is the keyboard-reachable route.
        getRowActions={(row) => [
          {
            key: 'queries',
            label: 'Queries',
            icon: <ListAltIcon fontSize="small" />,
            hint: 'Show the queries this request issued',
            onClick: () => setOpenId(row.id),
          },
        ]}
      />

      <Dialog open={openId != null} onClose={() => setOpenId(null)} maxWidth="md" fullWidth {...dialogProps}>
        <DialogTitle sx={{ pr: 6 }}>
          {current ? `${current.method} ${current.path}` : 'Slow request'}
          <IconButton
            onClick={() => setOpenId(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {detail.isLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detail.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => detail.refetch()}>
                  Retry
                </Button>
              }
            >
              Could not load this request.
            </Alert>
          ) : !current ? (
            <Typography variant="body2" color="text.secondary">
              That request is no longer retained.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Detail label="Duration" value={formatMs(current.durationMs)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Detail label="In database" value={formatMs(current.dbMs)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  {/* The remainder is application time: serialisation, business logic, external
                      calls. Naming it stops "slow request, fast queries" from reading as a puzzle. */}
                  <Detail label="In application" value={formatMs(current.durationMs - current.dbMs)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Detail label="Queries" value={formatCount(current.dbCommandCount)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Detail label="Status" value={String(current.statusCode)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Detail label="Service" value={current.service} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Detail label="When" value={formatDateTime(current.startedUtc)} />
                </Grid>
                <Grid item xs={12}>
                  <Detail label="Route template" value={current.route} mono />
                </Grid>
                {current.userId && (
                  <Grid item xs={12} sm={6}>
                    <Detail label="User" value={current.userId} mono />
                  </Grid>
                )}
                {current.traceId && (
                  <Grid item xs={12} sm={6}>
                    {/* Matches the trace id on the server log line for the same request. */}
                    <Detail label="Trace" value={current.traceId} mono />
                  </Grid>
                )}
              </Grid>

              {current.exception && (
                <Alert severity="error" variant="outlined">
                  {current.exception}
                </Alert>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Slowest queries in this request
                </Typography>

                {current.topQueries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No individual query crossed the capture threshold, so the time went somewhere other than
                    the database.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {current.topQueries.map((query, index) => (
                      <Box key={index}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{ mb: 0.5, flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {formatMs(query.ms)}
                          </Typography>
                          {query.occurrences > 1 && (
                            <Chip
                              label={`ran ${query.occurrences}× in this request`}
                              size="small"
                              color={query.occurrences > 10 ? 'error' : 'warning'}
                              variant="outlined"
                            />
                          )}
                        </Stack>
                        <SqlBlock sql={query.sql} maxLines={6} />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  )
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono ? 'ui-monospace, monospace' : undefined,
          fontSize: mono ? 12 : undefined,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Typography>
    </Stack>
  )
}
